import React, {useEffect, useRef, useState} from 'react'
import {FormattedMessage, defineMessages, useIntl} from 'react-intl'
import {
    Box,
    Button,
    Checkbox,
    Flex,
    Text,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useGigya} from '../../context/gigya'
import {useLocation} from 'react-router-dom'
import {useLocaleCountry} from '../../hooks/use-locale-custom'
import {BRAND_KEY} from '../../brand'
import {useForm} from 'react-hook-form'
import useGigyaSSR from '../../custom-hooks/use-gigya-ssr'
import Feedback from '../../components/feedback'
import LoadingSpinner from '../../components/loading-spinner'
// import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
// import {Helmet} from 'react-helmet'

export const messages = defineMessages({
    updating_preferences: {
        defaultMessage: 'Thank you for updating your preferences',
        id: 'unsubscribe.success.updating_preferences',
    },
    error_preference: {
        defaultMessage: 'Error updating user preferences',
        id: 'unsubscribe.error.updating_preferences',
    },
    user_not_found: {
        defaultMessage: 'User not found',
        id: 'unsubscribe.error.user_not_found',
    },
})

const UnsubscribePage = () => {
    const location = useLocation()
    const country = useLocaleCountry()
    const [message, setMessage] = useState(null)
    const intl = useIntl()
    const {gigyaObject} = useGigya()
    const {setAccountInfo, getAccountInfo, getLiteToken} = useGigyaSSR()

    const form = useForm()

    const [info, setInfo] = useState(null)
    const [isLoading, setIsLoading] = useState(true)
    const [showButtons, setShowButtons] = useState(true)

    const params = new URLSearchParams(location.search)
    const uid = params.get('uid')
    const email = params.get('email')
    let queryIdentifier = useRef({})
    // const {customParams} = getConfig()
    // const currentPath = 'https://' + customParams.currentHost + location.pathname
    // const webPageSchemaMarkup = {
    //     '@context': 'https://schema.org/',
    //     '@type': 'WebPage',
    //     name: 'Unsubscribe',
    //     description: 'Unsubscribe',
    //     url: currentPath,
    // }

    async function getUserInfo() {
        let data = await getAccountInfo(uid)
        queryIdentifier.current = {
            uid,
        }

        if (data?.errorDetails === 'Unknown user') {
            try {
                const response = await getLiteToken(email)
                data = await getAccountInfo(null, response?.token)
                queryIdentifier.current = {
                    regToken: response?.token,
                }
            } catch (e) {
                console.error(e)
            }
        }

        if (data?.profile?.email === email) {
            setInfo(data)
            // In the user info, the email is encoded with '+' instead of ' '
        } else if (data?.profile?.email === email.replaceAll(' ', '+')) {
            setInfo(data)
        }

        setIsLoading(false)
    }

    async function setUserInfo(params) {
        const response = await setAccountInfo(params)
        if (response.statusCode >= 300) {
            setMessage({message: messages.error_preference, error: true})
            return
        }
        setMessage({message: messages.updating_preferences, error: false})
    }

    useEffect(() => {
        if (!gigyaObject) return

        getUserInfo()
    }, [gigyaObject])

    if (isLoading)
        return (
            <Box height={'100wh'}>
                <LoadingSpinner />
            </Box>
        )

    if (!gigyaObject || !info)
        return (
            <Box layerStyle="page" data-testid="user-not-found-container">
                <Box my="1rem">
                    <Feedback
                        message={intl.formatMessage(messages.user_not_found)}
                        variant="removeFeedback"
                        status="error"
                        enableIcon={false}
                    />
                </Box>
            </Box>
        )

    const siteConsent = gigyaObject.thisScript.globalConf.consentMapping[country]

    const entitlements = info?.preferences?.[siteConsent]?.entitlements
    const subscriptions = info?.data?.SMS_subscription

    const entitlementKey = `${BRAND_KEY}${country}`

    const addOrRemoveFromArray = (isChecked, items = [], value) => {
        let i = [...items]

        if (isChecked) {
            const index = i.indexOf(value)
            if (index === -1) i.push(value)
        } else {
            const index = i.indexOf(value)

            if (index > -1) i = i.filter((item) => item !== value)
        }

        return i
    }

    const updateInfo = async (form) => {
        const e = addOrRemoveFromArray(form.email, entitlements, entitlementKey)

        await setUserInfo({
            ...queryIdentifier.current,
            preferences: {
                [siteConsent]: {
                    isConsentGranted: e.length > 0,
                    entitlements: e,
                },
            },
            data: {
                SMS_subscription: {
                    ...subscriptions,
                    [BRAND_KEY]: form.sms,
                },
            },
        })
    }

    const unsubscribeEmail = async () => {
        const e = addOrRemoveFromArray(false, entitlements, entitlementKey)
        await setUserInfo({
            ...queryIdentifier.current,
            preferences: {
                [siteConsent]: {
                    isConsentGranted: e.length > 0,
                    entitlements: e,
                },
            },
        })
        setShowButtons(false)
    }

    const isEntitlementChecked = entitlements?.includes(entitlementKey)
    const isSmsSubscriptionChecked = subscriptions?.[BRAND_KEY] ?? false

    return (
        <Box layerStyle="page">
            {/* <Helmet>
                <script type="application/ld+json">
                    {JSON.stringify(webPageSchemaMarkup, null, 2)}
                </script>
            </Helmet> */}
            {message?.message && (
                <Box my="1rem">
                    <Feedback
                        message={intl.formatMessage(message.message)}
                        variant="removeFeedback"
                        status={message.error ? 'error' : 'success'}
                        enableIcon={false}
                    />
                </Box>
            )}
            <Flex flexDirection="column" paddingBottom="2rem">
                <Box width={'100%'} backgroundColor={'background.brand'}>
                    <Text textStyle="head-xlarge" padding="3rem" color="text.inverse">
                        <FormattedMessage
                            defaultMessage="We are sorry to see you go"
                            id="unsubscribe.success.message"
                        />
                    </Text>
                </Box>
                <Flex flexDirection="column" textStyle="body-large" gap="1rem" padding={'0 2rem'}>
                    <Box marginTop="2rem">
                        <FormattedMessage
                            defaultMessage="Are you sure you want to unsubscribe from all marketing communications?"
                            id="unsubscribe.success.question"
                        />
                    </Box>
                    {gigyaObject.thisScript.globalConf.phonePrefixMapping?.[country] ? (
                        <Flex flexDirection="column" gap="1rem">
                            <FormattedMessage
                                defaultMessage="Manage the channels you are subscribed to"
                                id="unsubscribe.success.manage"
                            />

                            <form onSubmit={form.handleSubmit((d) => updateInfo(d))}>
                                <Flex flexDirection="column" gap="1rem">
                                    <Flex gap="1rem">
                                        <Checkbox
                                            {...form.register('email')}
                                            defaultChecked={isEntitlementChecked}
                                        >
                                            <FormattedMessage
                                                defaultMessage="Email"
                                                id="unsubscribe.email"
                                            />
                                        </Checkbox>
                                        {info.data?.phones?.mobile && (
                                            <Checkbox
                                                {...form.register('sms')}
                                                defaultChecked={isSmsSubscriptionChecked}
                                            >
                                                <FormattedMessage
                                                    defaultMessage="SMS"
                                                    id="unsubscribe.sms"
                                                />
                                            </Checkbox>
                                        )}
                                    </Flex>
                                    <Box>
                                        <Button
                                            variant="primary"
                                            type="submit"
                                            data-testid="submit-button"
                                        >
                                            <FormattedMessage
                                                defaultMessage="Update preferences"
                                                id="unsubscribe.success.update"
                                            />
                                        </Button>
                                    </Box>
                                </Flex>
                            </form>
                        </Flex>
                    ) : (
                        <Flex flexDirection="column" gap="1rem">
                            {showButtons ? (
                                isEntitlementChecked ? (
                                    <>
                                        <FormattedMessage
                                            defaultMessage="You are registered with the following email address"
                                            id="unsubscribe.success.email_registered"
                                        />
                                        <Box fontWeight="bold">{email}</Box>
                                        <Flex gap="1rem">
                                            <Button
                                                variant="secondary"
                                                onClick={() => {}}
                                                data-testid="unsubscribe-no-button"
                                            >
                                                <FormattedMessage
                                                    defaultMessage="No"
                                                    id="unsubscribe.no"
                                                />
                                            </Button>
                                            <Button
                                                variant="primary"
                                                onClick={() => unsubscribeEmail()}
                                                data-testid="unsubscribe-yes-button"
                                            >
                                                <FormattedMessage
                                                    defaultMessage="Yes"
                                                    id="unsubscribe.yes"
                                                />
                                            </Button>
                                        </Flex>
                                    </>
                                ) : (
                                    <>
                                        <FormattedMessage
                                            defaultMessage="You are not registered with the following email address"
                                            id="unsubscribe.success.email_not_registered"
                                        />
                                        <Box fontWeight="bold">{email}</Box>
                                    </>
                                )
                            ) : (
                                <>
                                    <FormattedMessage
                                        defaultMessage="You have now logged out."
                                        id="unsubscribe.success.logged_out"
                                    />
                                    <Text>
                                        <FormattedMessage
                                            defaultMessage="Back to home page"
                                            id="unsubscribe.success.back_to_home"
                                        />
                                    </Text>
                                </>
                            )}
                        </Flex>
                    )}
                </Flex>
            </Flex>
        </Box>
    )
}

export default UnsubscribePage
